import { useCallback, useEffect, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { COPILOT_TOPIC } from './useCopilotMessages';

function normalizeTranscriptLine(message) {
  return {
    id: message.utterance_id || `cap-${message.ts || Date.now()}`,
    speaker: message.speaker || '',
    text: String(message.text || '').trim(),
    partial: Boolean(message.partial),
    ts: message.ts || Date.now(),
  };
}

/**
 * Room-wide Sage transcript feed for captions (hosts and candidates).
 * Does not handle suggestion payloads.
 */
export function useCaptionsTranscript({ enabled = true } = {}) {
  const room = useRoomContext();
  const [transcriptLines, setTranscriptLines] = useState([]);

  const clearTranscript = useCallback(() => {
    setTranscriptLines([]);
  }, []);

  useEffect(() => {
    if (!enabled || !room) {
      return undefined;
    }

    const handleData = (payload, _participant, _kind, topic) => {
      if (topic && topic !== COPILOT_TOPIC) return;
      let message;
      try {
        const text =
          typeof payload === 'string'
            ? payload
            : new TextDecoder().decode(payload);
        message = JSON.parse(text);
      } catch {
        return;
      }
      if (!message || message.type !== 'copilot_transcript') return;

      const line = normalizeTranscriptLine(message);
      if (!line.text) return;

      setTranscriptLines((prev) => {
        if (line.partial) {
          const without = prev.filter((p) => p.id !== line.id || !p.partial);
          return [...without, line].slice(-40);
        }
        const withoutPartial = prev.filter((p) => p.id !== line.id);
        return [...withoutPartial, { ...line, partial: false }].slice(-40);
      });
    };

    room.on('dataReceived', handleData);
    return () => {
      room.off('dataReceived', handleData);
    };
  }, [enabled, room]);

  return { transcriptLines, clearTranscript };
}
